/**
 * Session Detail Modal - Full Analysis View
 * Shows all analysis sections from Review Center for a selected session
 * Allows principal to modify/approve sections
 * Sanjaya Brand Kit: Navy (#254B70), Orange (#F0A820), Teal (#258010)
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { 
  X, ChevronDown, ChevronUp, RefreshCw, CheckCircle, RotateCcw,
  FileText, Phone, Calendar, User, Baby, Clock, Star, AlertTriangle,
  BarChart3, TrendingUp, MessageSquare, Heart, Brain, Target, Save
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SessionDetailModal = ({ session, onClose, onUpdate }) => {
  const getToken = useCallback(() => localStorage.getItem('principal_token'), []);
  const [loading, setLoading] = useState(true);
  const [sessionData, setSessionData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    transcript: true,
    callReview: false,
    dailySummary: false,
    weeklyDigest: false,
    growthMilestones: false,
    diagnostics: false,
    alerts: false,
    monthlyReport: false
  });
  
  // Edit states
  const [editMode, setEditMode] = useState({});
  const [editedContent, setEditedContent] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (session) {
      loadFullSessionData();
    }
  }, [session]);

  const loadFullSessionData = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const sessionId = session.id || session.session_id;
      
      // Fetch all analysis data in parallel
      const [
        sessionRes,
        callReviewRes,
        dailySummaryRes,
        weeklyDigestRes,
        growthRes,
        diagnosticsRes,
        alertsRes
      ] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/principal/session/${sessionId}?token=${token}`).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/principal/session/${sessionId}/call-review?token=${token}`).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/principal/session/${sessionId}/daily-summary?token=${token}`).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/principal/session/${sessionId}/weekly-digest?token=${token}`).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/principal/session/${sessionId}/growth-milestones?token=${token}`).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/principal/session/${sessionId}/diagnostics?token=${token}`).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/principal/session/${sessionId}/alerts?token=${token}`).catch(() => ({ data: null }))
      ]);

      setSessionData({
        session: sessionRes.data?.session || sessionRes.data || session,
        transcript: sessionRes.data?.transcript || session.transcript,
        callReview: callReviewRes.data?.review || null,
        dailySummary: dailySummaryRes.data?.daily_summary || null,
        weeklyDigest: weeklyDigestRes.data?.digest || null,
        growthMilestones: growthRes.data?.milestones || null,
        diagnostics: diagnosticsRes.data?.diagnostics || null,
        alerts: alertsRes.data?.alerts || null
      });
    } catch (error) {
      console.error('Error loading session data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleEditMode = (section) => {
    setEditMode(prev => ({ ...prev, [section]: !prev[section] }));
    if (!editMode[section]) {
      // Initialize edit content when entering edit mode
      setEditedContent(prev => ({
        ...prev,
        [section]: getEditableContent(section)
      }));
    }
  };

  const getEditableContent = (section) => {
    switch(section) {
      case 'dailySummary':
        return sessionData?.dailySummary?.summary_for_parent || '';
      case 'transcript':
        return sessionData?.transcript || '';
      default:
        return '';
    }
  };

  const saveEdit = async (section) => {
    setSaving(true);
    try {
      const token = getToken();
      const sessionId = session.id || session.session_id;
      
      // Save based on section type
      if (section === 'dailySummary') {
        await axios.put(`${BACKEND_URL}/api/principal/session/${sessionId}/daily-summary`, null, {
          params: { token, summary: editedContent[section] }
        });
      } else if (section === 'transcript') {
        await axios.put(`${BACKEND_URL}/api/principal/session/${sessionId}/transcript`, null, {
          params: { token, transcript: editedContent[section] }
        });
      }
      
      setEditMode(prev => ({ ...prev, [section]: false }));
      loadFullSessionData(); // Reload data
      onUpdate?.();
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error saving changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', { 
        weekday: 'short', 
        day: 'numeric', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-sanjaya-navy mx-auto mb-3" />
          <p className="text-slate-600">Loading session details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-sanjaya-navy to-sanjaya-navy/90 text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Baby className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-lg font-bold">{session.child_name}</h2>
              <div className="flex items-center gap-3 text-sm text-white/80">
                <span className="flex items-center gap-1">
                  <User className="w-3.5 h-3.5" /> {session.observer_name}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" /> {formatDate(session.session_date)}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" /> {session.duration_minutes || 0} min
                </span>
              </div>
            </div>
          </div>
          <Button variant="ghost" onClick={onClose} className="text-white hover:bg-white/20">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Session Transcript */}
          <Card className="border-2 border-teal-200">
            <CardHeader 
              className="pb-2 cursor-pointer hover:bg-teal-50 transition-colors"
              onClick={() => toggleSection('transcript')}
            >
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-teal-800 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Session Transcript
                </CardTitle>
                <div className="flex items-center gap-2">
                  {!editMode.transcript && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => { e.stopPropagation(); toggleEditMode('transcript'); }}
                      className="h-7 text-xs"
                    >
                      Edit
                    </Button>
                  )}
                  {expandedSections.transcript ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </div>
            </CardHeader>
            {expandedSections.transcript && (
              <CardContent className="p-4 pt-2">
                {editMode.transcript ? (
                  <div className="space-y-2">
                    <Textarea
                      value={editedContent.transcript || sessionData?.transcript || ''}
                      onChange={(e) => setEditedContent(prev => ({ ...prev, transcript: e.target.value }))}
                      className="min-h-[200px] text-sm"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => saveEdit('transcript')} disabled={saving} className="bg-teal-600">
                        {saving ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                        Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => toggleEditMode('transcript')}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-teal-50 p-3 rounded-lg max-h-[300px] overflow-y-auto">
                    <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">
                      {sessionData?.transcript || 'No transcript available'}
                    </pre>
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {/* Call Review */}
          {sessionData?.callReview?.status === 'generated' && (
            <Card className="border-2 border-sky-200">
              <CardHeader 
                className="pb-2 cursor-pointer hover:bg-sky-50 transition-colors"
                onClick={() => toggleSection('callReview')}
              >
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold text-sky-800 flex items-center gap-2">
                    <Phone className="w-4 h-4" /> Call Review (Observer Insights)
                    <span className="text-xs font-normal bg-sky-100 px-2 py-0.5 rounded-full">
                      Session #{sessionData.callReview.session_number}
                    </span>
                  </CardTitle>
                  {expandedSections.callReview ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </CardHeader>
              {expandedSections.callReview && (
                <CardContent className="p-4 pt-2 space-y-3">
                  {/* Summary */}
                  <div className="bg-sky-50 p-3 rounded-lg">
                    <p className="text-sm text-slate-700">{sessionData.callReview.review_data?.summary?.what_happened}</p>
                    <div className="flex gap-2 mt-2">
                      <span className="text-xs px-2 py-0.5 bg-sky-100 rounded-full">
                        Mood: {sessionData.callReview.review_data?.summary?.child_mood}
                      </span>
                      <span className="text-xs px-2 py-0.5 bg-sky-100 rounded-full">
                        Engagement: {sessionData.callReview.review_data?.summary?.engagement_level}
                      </span>
                    </div>
                  </div>
                  
                  {/* Topics */}
                  {sessionData.callReview.review_data?.topics_discussed?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-sky-700 mb-1">Topics Discussed</p>
                      <div className="flex flex-wrap gap-1">
                        {sessionData.callReview.review_data.topics_discussed.map((t, i) => (
                          <span key={i} className="text-xs bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full">
                            {t.topic} ({t.child_interest})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Coaching Tips */}
                  {sessionData.callReview.review_data?.coaching_tips?.length > 0 && (
                    <div className="bg-teal-50 p-3 rounded-lg">
                      <p className="text-xs font-semibold text-teal-700 mb-2">🎯 Coaching Tips</p>
                      {sessionData.callReview.review_data.coaching_tips.map((tip, i) => (
                        <div key={i} className="text-xs text-slate-600 mb-1">
                          <strong>{tip.tip}</strong> — {tip.example}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          )}

          {/* Daily Summary */}
          {sessionData?.dailySummary && (
            <Card className="border-2 border-amber-200">
              <CardHeader 
                className="pb-2 cursor-pointer hover:bg-amber-50 transition-colors"
                onClick={() => toggleSection('dailySummary')}
              >
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" /> Daily Insight Summary (For Parent)
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {!editMode.dailySummary && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); toggleEditMode('dailySummary'); }}
                        className="h-7 text-xs"
                      >
                        Edit
                      </Button>
                    )}
                    {expandedSections.dailySummary ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </div>
              </CardHeader>
              {expandedSections.dailySummary && (
                <CardContent className="p-4 pt-2">
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="bg-amber-50 px-3 py-2 rounded text-center">
                      <p className="text-[10px] text-amber-600">Mood</p>
                      <p className="font-bold text-amber-800 capitalize">{sessionData.dailySummary.mood}</p>
                    </div>
                    <div className="bg-amber-50 px-3 py-2 rounded text-center">
                      <p className="text-[10px] text-amber-600">Curiosity</p>
                      <p className="font-bold text-amber-800">{sessionData.dailySummary.curiosity_index}/10</p>
                    </div>
                    <div className="bg-amber-50 px-3 py-2 rounded text-center">
                      <p className="text-[10px] text-amber-600">Key Topics</p>
                      <p className="text-xs text-amber-700">{sessionData.dailySummary.key_topics?.slice(0, 2).join(', ')}</p>
                    </div>
                  </div>
                  
                  {editMode.dailySummary ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editedContent.dailySummary || sessionData.dailySummary.summary_for_parent || ''}
                        onChange={(e) => setEditedContent(prev => ({ ...prev, dailySummary: e.target.value }))}
                        className="min-h-[100px] text-sm"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => saveEdit('dailySummary')} disabled={saving} className="bg-amber-600">
                          {saving ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                          Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => toggleEditMode('dailySummary')}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-700 bg-amber-50 p-3 rounded-lg">
                      {sessionData.dailySummary.summary_for_parent}
                    </p>
                  )}
                </CardContent>
              )}
            </Card>
          )}

          {/* Weekly Digest */}
          {sessionData?.weeklyDigest && (
            <Card className="border-2 border-blue-200">
              <CardHeader 
                className="pb-2 cursor-pointer hover:bg-blue-50 transition-colors"
                onClick={() => toggleSection('weeklyDigest')}
              >
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold text-blue-800 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" /> Weekly Digest
                    <span className="text-xs font-normal bg-blue-100 px-2 py-0.5 rounded-full">
                      Week {sessionData.weeklyDigest.week_number} of {sessionData.weeklyDigest.total_weeks}
                    </span>
                  </CardTitle>
                  {expandedSections.weeklyDigest ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </CardHeader>
              {expandedSections.weeklyDigest && (
                <CardContent className="p-4 pt-2">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm text-slate-700">{sessionData.weeklyDigest.digest_text}</p>
                  </div>
                  {sessionData.weeklyDigest.key_themes?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {sessionData.weeklyDigest.key_themes.map((theme, i) => (
                        <span key={i} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{theme}</span>
                      ))}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          )}

          {/* Growth Milestones */}
          {sessionData?.growthMilestones && (
            <Card className="border-2 border-purple-200">
              <CardHeader 
                className="pb-2 cursor-pointer hover:bg-purple-50 transition-colors"
                onClick={() => toggleSection('growthMilestones')}
              >
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold text-purple-800 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" /> Growth Milestones
                  </CardTitle>
                  {expandedSections.growthMilestones ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </CardHeader>
              {expandedSections.growthMilestones && (
                <CardContent className="p-4 pt-2">
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(sessionData.growthMilestones.areas || {}).map(([key, data]) => (
                      <div key={key} className="bg-purple-50 p-2 rounded">
                        <p className="text-xs font-medium text-purple-700 capitalize">{key.replace(/_/g, ' ')}</p>
                        <p className="text-lg font-bold text-purple-800">{data.score}/10</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Diagnostics */}
          {sessionData?.diagnostics?.categories && (
            <Card className="border-2 border-cyan-200">
              <CardHeader 
                className="pb-2 cursor-pointer hover:bg-cyan-50 transition-colors"
                onClick={() => toggleSection('diagnostics')}
              >
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold text-cyan-800 flex items-center gap-2">
                    <Brain className="w-4 h-4" /> Diagnostic Categories
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      sessionData.diagnostics.overall_status === 'thriving' ? 'bg-green-100 text-green-700' :
                      sessionData.diagnostics.overall_status === 'developing_well' ? 'bg-blue-100 text-blue-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {sessionData.diagnostics.overall_status?.replace(/_/g, ' ').toUpperCase()}
                    </span>
                  </CardTitle>
                  {expandedSections.diagnostics ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </CardHeader>
              {expandedSections.diagnostics && (
                <CardContent className="p-4 pt-2 space-y-2">
                  {Object.entries(sessionData.diagnostics.categories).map(([key, cat]) => (
                    <div key={key} className="bg-cyan-50 p-2 rounded">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-cyan-800">
                          {cat.icon} {cat.code}. {cat.category_name}
                        </span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                          cat.overall_level === 'high' ? 'bg-green-100 text-green-700' :
                          cat.overall_level === 'moderate' ? 'bg-yellow-100 text-yellow-700' :
                          cat.overall_level === 'NA' ? 'bg-slate-100 text-slate-600' :
                          'bg-orange-100 text-orange-700'
                        }`}>
                          {cat.overall_level?.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>
          )}

          {/* Flag Detection */}
          {sessionData?.alerts && (
            <Card className="border-2 border-red-200">
              <CardHeader 
                className="pb-2 cursor-pointer hover:bg-red-50 transition-colors"
                onClick={() => toggleSection('alerts')}
              >
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold text-red-800 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> Flag Detection
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      sessionData.alerts.summary?.level === 'urgent' ? 'bg-red-100 text-red-700' :
                      sessionData.alerts.summary?.level === 'concern' ? 'bg-orange-100 text-orange-700' :
                      sessionData.alerts.summary?.level === 'watch' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {sessionData.alerts.summary?.status || 'CLEAR'}
                    </span>
                  </CardTitle>
                  {expandedSections.alerts ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </CardHeader>
              {expandedSections.alerts && (
                <CardContent className="p-4 pt-2">
                  {sessionData.alerts.flags?.length > 0 ? (
                    <div className="space-y-2">
                      {sessionData.alerts.flags.map((flag, i) => (
                        <div key={i} className={`p-2 rounded ${
                          flag.level === 'urgent' ? 'bg-red-50 border border-red-200' :
                          flag.level === 'concern' ? 'bg-orange-50 border border-orange-200' :
                          'bg-yellow-50 border border-yellow-200'
                        }`}>
                          <p className="text-xs font-medium">{flag.category}</p>
                          <p className="text-xs text-slate-600">{flag.description}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-green-600 text-center py-4">
                      ✅ No flags detected in this session
                    </p>
                  )}
                </CardContent>
              )}
            </Card>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-between items-center bg-slate-50">
          <div className="text-xs text-slate-500">
            Session ID: {session.id || session.session_id}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadFullSessionData}>
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
            <Button onClick={onClose} className="bg-sanjaya-navy">
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionDetailModal;
